import { useDispatch, useSelector } from 'react-redux';
import {
  setPrevAgentStep,
  collectPollCtaOptionIdsFromEngagements,
} from '../../../../../../redux/slices/agents/agentSlice';
import {
  useGetAllDomainsQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
} from '../../../../../../services/api';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import agentData from '../../../../../../constants/agents/initialStates/agentData';
import useDraftsHandler from '../../../../../../utils/controllers/useDraftsHandler';
import { useState } from 'react';
import extractHost from '../../../../../../utils/controllers/extractHost';
import {
  KA_REQUIRED_FIELDS_OPTIONS_BY_TYPE,
  canUseCustomRecommendationTypes,
  isCustomRequiredField,
  normalizePersonalizationToolType,
} from '../../../../../../constants/agents/kaPersonalizationRequiredFields';
import useEventScope from '../../../../../events/controllers/useEventScope';
import {
  withEventScopeForCreate,
  withEventScopeForUpdate,
} from '../../../../../events/controllers/eventScopePayload';
import { getEventContextPath } from '../../../../../events/controllers/eventFormatters';
import { normalizeVoiceConfig } from '../../../../../../constants/agents/voiceConfig';

const XRAY_DEFAULT_QUESTIONS = [
  {
    language: 'en',
    summary: "What's the summary?",
    keyPeople: 'Who are the key people?',
    keyFact: 'What are the key facts?',
    quotes: 'What are the quotes?',
  },
  {
    language: 'es',
    summary: 'En resumen...',
    keyPeople: 'Protagonistas',
    keyFact: '¿Por qué es importante?',
    quotes: 'Declaraciones',
  },
  {
    language: 'nl',
    summary: 'Wat is de samenvatting?',
    keyPeople: 'Wie zijn de belangrijke personen?',
    keyFact: 'Wat zijn de belangrijke feiten?',
    quotes: 'Wat zijn de citaten?',
  },
  {
    language: 'lv',
    summary: "What's the summary?",
    keyPeople: 'Who are the key people?',
    keyFact: 'What are the key facts?',
    quotes: 'What are the quotes?',
  },
  {
    language: 'ua',
    summary: 'Який короткий зміст?',
    keyPeople: 'Хто ключові люди?',
    keyFact: 'Які ключові факти?',
    quotes: 'Які цитати?',
  },
  {
    language: 'ru',
    summary: 'Каково краткое изложение?',
    keyPeople: 'Кто является ключевыми людьми?',
    keyFact: 'Какие ключевые факты?',
    quotes: 'Какие цитаты?',
  },
  {
    language: 'cat',
    summary: 'Quina és la resum?',
    keyPeople: 'Qui són les persones clau?',
    keyFact: 'Quins són els fets clau?',
    quotes: 'Quines són les cites?',
  },
  {
    language: 'da',
    summary: 'Hvad er resum?',
    keyPeople: 'Hvem er de vigtigste personer?',
    keyFact: 'Hvad er de vigtigste fakta?',
    quotes: 'Hvad er citaterne?',
  },
  {
    language: 'est',
    summary: 'Mis on kokkuvõte?',
    keyPeople: 'Kes on võtmeisikud?',
    keyFact: 'Mis on võtmefaktid?',
    quotes: 'Mis on tsitaadid?',
  },
];

/** Keep first `{ screenId }` per id; drops later duplicates. */
const dedupeCampaignActionsByScreenId = actions => {
  if (!Array.isArray(actions)) return [];
  const seen = new Set();
  return actions?.filter(item => {
    const sid = item?.screenId;
    if (seen.has(sid)) return false;
    seen.add(sid);
    return true;
  });
};

const useAgentHandler = () => {
  const [isLaunch, setIsLaunch] = useState(false);
  const navigate = useNavigate();
  const agent = useSelector(state => state.agent.data);
  const dispatch = useDispatch();
  const creationType = useSelector(state => state.agent.etc.creationType);
  const engagements = useSelector(state => state.agent.data.campaignData.engagements);
  const engagementSelection = useSelector(state => state.agent.etc.engagementSelection);
  const isShowStaticCTAs = useSelector(state => state.agent.etc.cta.isShowStaticCTAs);
  const intentBasedCtaIsActive = useSelector(state => state.agent.etc.intentBasedCtaIsActive);
  const showMandatoryCTA = useSelector(state => state.agent.etc.showMandatoryCTA);
  const pollCTAsUsageType = useSelector(state => state.agent.etc.pollCtaMapping.pollCTAsUsageType);
  const ABtestActions = useSelector(state => state.agent.etc.pollCtaMapping.ABtestActions);
  const mappedOptions = useSelector(state => state.agent.etc.pollCtaMapping.mappedOptions);
  const search = useLocation().search;
  const _id = new URLSearchParams(search).get('_id');
  const redirect = new URLSearchParams(search).get('redirect');
  const { data: allDomains } = useGetAllDomainsQuery();
  const [_CREATE_AGENT, { isLoading: isCreating }] = useCreateAgentMutation();
  const [_UPDATE_AGENT, { isLoading: isUpdating }] = useUpdateAgentMutation();
  const { handleDeleteDraft } = useDraftsHandler({
    draftType: agentData.draft.value,
  });
  const { isEventContext, eventId, workflowId, workflowKey } = useEventScope();

  const onPrevHandler = () => {
    dispatch(setPrevAgentStep());
  };

  const onSubmitHandler = ({ isLaunch = false, aiEngagementsNeedUpdate }) => {
    setIsLaunch(isLaunch);
    switch (creationType) {
      case 'create':
        return createAgent(isLaunch);
      case 'edit':
        return editAgent(isLaunch, aiEngagementsNeedUpdate);
      case 'draft':
        return createAgent(isLaunch);
      default:
        return createAgent(isLaunch);
    }
  };

  const prepareXrayQuestions = (questions, language) => {
    return questions.map(question => {
      const defaultQuestion = XRAY_DEFAULT_QUESTIONS.find(item => item.language === language);

      if (defaultQuestion && !question.questionText) {
        return {
          ...question,
          questionText: defaultQuestion[question.type] || `What's the ${question.type}?`,
        };
      }

      return question;
    });
  };

  const formatStyleProperty = values => {
    return values.map(value => `${value}px`).join(' ');
  };

  const prepareCTAMappingsForPollingAgent = () => {
    let linkedActions = [];
    let actions = [];

    if (pollCTAsUsageType !== 'abTest') {
      linkedActions =
        mappedOptions
          ?.map(option => {
            const id = option?.actionIds?.[0]?._id;
            return id
              ? {
                  optionId: option?.optionId,
                  actionIds: [id],
                }
              : null;
          })
          ?.filter(Boolean) || [];

      const validOptionIds = collectPollCtaOptionIdsFromEngagements(engagements);
      linkedActions = linkedActions.filter(la => validOptionIds.has(la.optionId));
    }

    // Build actions based on usage type
    if (pollCTAsUsageType === 'abTest') {
      actions =
        ABtestActions?.filter(action => action?._id)?.map(action => ({
          screenId: action._id,
        })) || [];
    } else {
      actions =
        linkedActions?.map(action => ({
          screenId: action?.actionIds?.[0],
        })) || [];
    }

    return { linkedActions, actionsArray: actions };
  };

  const prepareAgentData = async aiEngagementsNeedUpdate => {
    // Find domain ID
    const domainId = allDomains?.data?.find(
      item => item?.host === extractHost(agent?.domainConfig?.domainId)
    )?._id;

    // Extract data from agent state
    const endScreen = agent?.campaignData?.endScreen?._id;
    const campaignType = agent?.campaignData?.campaignType;
    const engagements =
      engagementSelection === 'aiGenerated' || engagementSelection === 'disableEngagement'
        ? []
        : agent?.campaignData?.engagements?.map(data => data?._id);

    let actions = agent?.campaignData?.actions || [];

    if (campaignType === 'recirculation' && !agent?.campaignData?.gatedEngagement) {
      actions = actions?.filter(action => action?.actionData?.actionType === 'link');
    }

    // Gated Response CTA off: never send response CTA id / count to the backend
    const isGatedResponseCtaActive = campaignType === 'rag' && showMandatoryCTA;
    if (campaignType === 'rag' && !isGatedResponseCtaActive) {
      actions = [];
    }

    const { linkedActions, actionsArray } = prepareCTAMappingsForPollingAgent();

    const actionIds = dedupeCampaignActionsByScreenId(
      campaignType === 'pollingAgent'
        ? actionsArray
        : actions?.map(action => ({ screenId: action?._id }))
    );

    const engineId = agent?.campaignData?.engineId?._id;

    // Format style properties
    const borderRadius = formatStyleProperty(agent?.visualization?.cardStyle?.borderRadius);
    const margin = formatStyleProperty(agent?.visualization?.cardStyle?.margin);

    // Prepare initial data
    let sentData = {
      ...agent,
    };

    let xrayQuestions = sentData?.campaignData?.xrayQuestions?.map(question => ({ ...question }));

    if (agent?.campaignData?.campaignType === 'xRay') {
      const language = agent?.visualization?.language;
      xrayQuestions = prepareXrayQuestions(xrayQuestions, language);
    }

    //clean the linked intents
    const linkedIntents = intentBasedCtaIsActive
      ? sentData?.campaignData?.linkedIntents?.filter(intent => Boolean(intent?.actionId))
      : [];

    const personalizationCfg = sentData.campaignData?.personalizationTool ?? {};
    const personalizationType =
      normalizePersonalizationToolType(personalizationCfg.type) || 'registrant';
    const validRequiredFieldValues = new Set(
      (KA_REQUIRED_FIELDS_OPTIONS_BY_TYPE[personalizationType] ?? []).map(opt => opt.value)
    );
    const ctasCfg = personalizationCfg.ctasConfig ?? {
      userInputCTAId: personalizationCfg.userInputCTAId,
      registrationCTAId: personalizationCfg.registrationCTAId,
      hasExportResult: personalizationCfg.hasExportResult,
    };
    const hasExportResult = ctasCfg.hasExportResult ?? false;

    // Prepare final data
    return {
      ...sentData,
      domainConfig: {
        ...agent?.domainConfig,
        domainId: domainId,
      },
      visualization: {
        ...agent?.visualization,
        cardStyle: {
          ...agent?.visualization?.cardStyle,
          borderRadius,
          margin,
        },
      },
      campaignData: {
        ...sentData.campaignData,
        engagementGenerationByAI:
          campaignType === 'rag' ? true : sentData.campaignData?.engagementGenerationByAI,
        gatedEngagement: isGatedResponseCtaActive
          ? Boolean(sentData.campaignData?.gatedEngagement)
          : campaignType === 'rag'
            ? false
            : sentData.campaignData?.gatedEngagement,
        showActionAfterResponses: isGatedResponseCtaActive
          ? sentData.campaignData?.showActionAfterResponses || 0
          : campaignType === 'rag'
            ? 0
            : sentData.campaignData?.showActionAfterResponses || 0,
        ...(sentData.campaignData?.actionBucketId?._id && {
          actionBucketId: sentData.campaignData?.actionBucketId?._id,
        }),
        ...(sentData.campaignData?.aiSimilarityActionBucketId?._id && {
          aiSimilarityActionBucketId: sentData.campaignData?.aiSimilarityActionBucketId?._id,
        }),
        emailCollectionScreen:
          campaignType === 'rag' && !isGatedResponseCtaActive
            ? null
            : sentData.campaignData?.emailCollectionScreen?._id
              ? {
                  screenId: sentData.campaignData.emailCollectionScreen._id,
                }
              : null,
        ...(sentData.campaignData?.actionBucketIds?.length > 0 && {
          actionBucketIds: sentData.campaignData?.actionBucketIds?.map(item => item?._id),
        }),
        engineId: engineId || '',
        endScreen: endScreen ? { screenId: endScreen } : null,
        engagements: engagements.map(_id => ({ screenId: _id })),
        actions: actionIds,
        ...(sentData.campaignData?.campaignType === 'xRay' && {
          linkActions: sentData.campaignData?.linkActions?.map(item => ({
            screenId: item?._id,
          })),
          xrayQuestions,
        }),
        staticReferences: isShowStaticCTAs
          ? sentData.campaignData?.staticReferences?.map(item => ({
              screenId: item?._id,
            }))
          : [],
        linkedIntents,
        linkedActions,
        // Voice conversation is for Knowledge agents only; the API turns it off when left out.
        voiceConfig:
          campaignType === 'rag'
            ? normalizeVoiceConfig(sentData.campaignData?.voiceConfig)
            : undefined,
        personalizationTool: {
          isActive: personalizationCfg.isActive ?? false,
          ctasConfig: {
            userInputCTAId: ctasCfg.userInputCTAId?._id || ctasCfg.userInputCTAId || '',
            registrationCTAId: ctasCfg.registrationCTAId?._id || ctasCfg.registrationCTAId || '',
            hasExportResult,
          },
          lockResult: personalizationCfg.lockResult ?? false,
          isPTStreaming: personalizationCfg.isPTStreaming ?? false,
          type: personalizationType,
          interfaceVariant: personalizationCfg.interfaceVariant || 'V2',
          // The team's labels (`custom/<labelId>`) are checked against its labels by the API.
          requiredFields: (personalizationCfg.requiredFields || []).filter(
            field =>
              validRequiredFieldValues.has(field) ||
              (isCustomRequiredField(field) && canUseCustomRecommendationTypes(personalizationType))
          ),
          actionBucketIds: (personalizationCfg.actionBucketIds || [])
            .map(item => item?._id || item)
            .filter(Boolean),
          ...(hasExportResult &&
            (personalizationCfg.exportEmailConfig?.provider ?? 'bridged') === 'graphAPI' && {
              exportEmailConfig: {
                provider: 'graphAPI',
                mailbox: personalizationCfg.exportEmailConfig?.mailbox || '',
                clientId: personalizationCfg.exportEmailConfig?.clientId || '',
                clientSecret: personalizationCfg.exportEmailConfig?.clientSecret || '',
                authority: personalizationCfg.exportEmailConfig?.authority || '',
              },
            }),
        },
      },
      ...(aiEngagementsNeedUpdate !== undefined && { aiEngagementsNeedUpdate }),
    };
  };

  const createAgent = async (isLaunch = false) => {
    try {
      const preparedData = await prepareAgentData();
      const payload = withEventScopeForCreate(preparedData, {
        isEventContext,
        eventId,
        workflowId,
      });

      const response = await _CREATE_AGENT({ ...payload, pauseCampaign: !isLaunch });

      if (response?.data?.success) {
        toast.success(isLaunch ? 'Agent published' : 'Agent created');

        if (creationType === 'draft') {
          handleDeleteDraft(_id);
        }

        navigate(
          isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
        );
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't create the agent"
        );
      }
    } catch (error) {
      toast.error("Couldn't create the agent");
    }
  };

  const editAgent = async (isLaunch = false, aiEngagementsNeedUpdate) => {
    try {
      const preparedData = await prepareAgentData(aiEngagementsNeedUpdate);
      const payload = withEventScopeForUpdate(
        preparedData,
        { isEventContext, eventId, workflowId },
        { eventId: agent?.eventId, workflowId: agent?.workflowId }
      );
      const response = await _UPDATE_AGENT({ ...payload, pauseCampaign: !isLaunch });

      if (response?.data?.success) {
        toast.success('Changes saved');

        if (redirect === 'overview') {
          navigate(`${agentData?.overviewPath}?_id=${_id}`);
        } else {
          navigate(
            isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
          );
        }
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't save the changes"
        );
      }
    } catch (error) {
      toast.error("Couldn't save the changes");
    }
  };

  return { onPrevHandler, onSubmitHandler, isCreating, isUpdating, isLaunch };
};

export default useAgentHandler;
